import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, Cookie, Clock, ChefHat, Printer, Mail, Send } from "lucide-react";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import PrintSheet, { PRINT_STYLES } from "@/components/PrintSheet";
import { FROLLA_VARIANTS, FROLLA_STEPS, PASTA_ZUCCHERO, BASI_TORTE } from "@/data/content";

const scaleItem = (item, n) => {
  // dose base = 24 biscotti da circa 27-30 g l'uno
  const raw = (item.qty * n) / 24;
  if (item.round === "g5") return `${Math.max(5, Math.round(raw / 5) * 5)} g`;
  if (item.round === "half") return `${Math.max(0.5, Math.round(raw * 2) / 2)}`;
  if (item.round === "tenth") return `${Math.round(raw * 10) / 10} ${raw > 1 ? item.unit.replace(/o$/, "i") : item.unit}`;
  return `${item.unit}`;
};

// Stampa nativa compatibile con mobile: niente popup, usa l'area .print-only
const printRecipe = () => {
  try {
    window.print();
  } catch {
    toast.error("Stampa non riuscita: apri il menu del browser e scegli «Stampa».");
  }
};

const PAN_SIZES = [16, 18, 20, 22, 24, 26, 28];

const scalePan = (item, size, base) => {
  const raw = item.qty * Math.pow(size / base, 2);
  if (item.round === "g5") return `${Math.max(5, Math.round(raw / 5) * 5)} g`;
  if (item.round === "half") return `${Math.max(0.5, Math.round(raw * 2) / 2)} g`;
  return item.unit;
};

const PanSpagnaCard = ({ base }) => {
  const [size, setSize] = useState(base.baseSize);
  return (
    <article
      data-testid={`base-torta-${base.id}`}
      className="h-full rounded-[1.75rem] border border-amber-100 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(45,36,36,0.1)] sm:p-8"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-menta px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-menta-dark">
        Ricetta Gratuita
      </span>
      <h4 className="mt-3 font-display text-lg font-bold text-cacao">{base.title}</h4>
      <p className="mt-1 text-xs text-cacao-muted">{base.subtitle}</p>
      <div className="mt-4">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-oro">
          Dimensione della teglia tonda
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="pan-size-picker">
          {PAN_SIZES.map((s) => (
            <button
              key={s}
              data-testid={`pan-size-${s}`}
              onClick={() => setSize(s)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                size === s
                  ? "bg-rosa text-white shadow-[0_6px_18px_rgba(232,122,144,0.4)]"
                  : "border border-pink-200 bg-crema text-cacao-muted hover:border-rosa"
              }`}
            >
              {s} cm
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-oro">
            Ingredienti · teglia da {size} cm
          </p>
          <ul className="space-y-1.5">
            {base.items.map((item, j) => (
              <li key={j} className="flex items-baseline justify-between gap-2 text-sm" data-testid={`base-${base.id}-ing-${j + 1}`}>
                <span className="text-cacao-muted">{item.name}</span>
                <span className="font-display font-bold text-cacao">{scalePan(item, size, base.baseSize)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-oro">Procedimento</p>
          <ol className="space-y-2">
            {base.steps.map((step, j) => (
              <li key={j} className="flex items-start gap-2.5 text-sm text-cacao-muted leading-relaxed" data-testid={`base-${base.id}-step-${j + 1}`}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blush font-display text-[11px] font-bold text-rosa-dark">
                  {j + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      </div>
      <p className="mt-5 font-hand text-xl text-oro">{base.note}</p>
    </article>
  );
};

const MorettoCard = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "moretto" }),
      });
      if (!res.ok) throw new Error();
      toast.success("Fatto! La ricetta della Torta Moretto è in viaggio verso la tua email.");
      setEmail("");
    } catch {
      toast.error("Qualcosa è andato storto. Riprova tra poco!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <article
      data-testid="base-torta-moretto"
      className="relative flex h-full flex-col overflow-hidden rounded-[1.75rem] bg-cacao p-6 sm:p-8"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 70% 60% at 80% 0%, rgba(217,119,6,0.3), transparent 65%)" }}
      />
      <div className="relative flex flex-1 flex-col">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-oro px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
          <Mail className="h-3 w-3" /> Gratis via email
        </span>
        <h4 className="mt-4 font-display text-lg font-bold text-crema">Torta Moretto</h4>
        <p className="mt-1 text-xs text-crema/60">
          La base al cioccolato più soffice · teglia da 20 cm
        </p>
        <p className="mt-4 flex-1 text-sm text-crema/75 leading-relaxed">
          La ricetta completa te la regalo: lascia la tua email e la ricevi
          subito, pronta da stampare e tenere in cucina.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="la-tua@email.it"
            data-testid="moretto-email-input"
            className="w-full rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm text-crema outline-none transition-colors placeholder:text-crema/40 focus:border-rosa"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="moretto-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-rosa px-6 py-3 text-sm font-bold text-white shadow-[0_10px_30px_rgba(232,122,144,0.4)] transition-colors hover:bg-rosa-dark disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {loading ? "Invio..." : "Ricevi la ricetta gratis"}
          </button>
        </form>
      </div>
    </article>
  );
};

const Calculator = () => {
  const [count, setCount] = useState(12);
  const [variant, setVariant] = useState("classica");
  const v = FROLLA_VARIANTS[variant];
  const fill = useMemo(() => `${((count - 6) / (48 - 6)) * 100}%`, [count]);

  return (
    <div
      id="calcolatore"
      data-testid="pasta-frolla-calculator"
      className="relative overflow-hidden rounded-[2rem] border border-pink-100 bg-white p-6 shadow-[0_20px_60px_rgba(45,36,36,0.08)] sm:p-10"
    >
      <PrintSheet
        id="print-sheet-frolla"
        eyebrow="LE ALLEGRE PASTICCIONE"
        title="Pasta Frolla per Biscotti Decorati"
        subtitle={`Variante: ${v.label} · Dosi per ${count} biscotti`}
      >
        <h2 style={PRINT_STYLES.h2}>Ingredienti</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {v.items.map((it) => (
              <tr key={it.name}>
                <td style={PRINT_STYLES.td}>{it.name}</td>
                <td style={{ ...PRINT_STYLES.td, textAlign: "right", fontWeight: 700 }}>{scaleItem(it, count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 style={PRINT_STYLES.h2}>Procedimento</h2>
        <ol style={PRINT_STYLES.list}>
          {FROLLA_STEPS.map((s) => (
            <li key={s.title} style={{ marginBottom: 8 }}>
              <strong>{s.title}.</strong> {s.text}
            </li>
          ))}
        </ol>
        <p style={PRINT_STYLES.small}>
          Riposo in frigo: almeno 30 minuti · Cottura: forno statico 180°C per 12-14 minuti. Consiglio: non
          lavorare troppo l'impasto, i biscotti resteranno perfetti e lisci.
        </p>
        <h2 style={PRINT_STYLES.h2}>Bonus: la Pasta di Zucchero fatta in casa</h2>
        <ul style={PRINT_STYLES.list}>
          {PASTA_ZUCCHERO.ingredients.map((ing, j) => (
            <li key={j}>{ing}</li>
          ))}
        </ul>
        <ol style={PRINT_STYLES.list}>
          {PASTA_ZUCCHERO.steps.map((step, j) => (
            <li key={j} style={{ marginBottom: 6 }}>
              {step}
            </li>
          ))}
        </ol>
      </PrintSheet>
      <div className="spotlight-gold pointer-events-none absolute inset-0" />
      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Eyebrow>Lead Magnet Gratuito</Eyebrow>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-cacao sm:text-3xl">
              La Pasta Frolla Perfetta
            </h2>
            <p className="mt-2 max-w-md text-sm text-cacao-muted leading-relaxed">
              Scegli la variante, decidi quanti biscotti vuoi decorare: le dosi
              si ricalcolano da sole.
            </p>
          </div>
          <div className="rounded-2xl bg-blush px-5 py-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-rosa-dark">
              Biscotti
            </p>
            <p
              data-testid="calculator-cookie-count"
              className="font-display text-4xl font-extrabold text-rosa-dark"
            >
              {count}
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-6">
            <div>
              <label
                htmlFor="variant-select"
                className="mb-2 block text-xs font-bold uppercase tracking-widest text-cacao-light"
              >
                Variante di frolla
              </label>
              <select
                id="variant-select"
                data-testid="calculator-variant-select"
                value={variant}
                onChange={(e) => setVariant(e.target.value)}
                className="w-full appearance-none rounded-2xl border border-pink-200 bg-crema px-4 py-3 text-sm font-semibold text-cacao outline-none transition-colors focus:border-rosa"
              >
                {Object.entries(FROLLA_VARIANTS).map(([key, val]) => (
                  <option key={key} value={key}>
                    {val.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 font-hand text-xl text-rosa-dark">{v.note}</p>
            </div>
            <div>
              <label className="mb-3 block text-xs font-bold uppercase tracking-widest text-cacao-light">
                Quanti biscotti?
              </label>
              <div className="flex items-center gap-3">
                <button
                  data-testid="calculator-stepper-minus"
                  onClick={() => setCount((c) => Math.max(6, c - 2))}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-pink-200 bg-white text-rosa-dark transition-colors hover:bg-blush"
                  aria-label="Riduci numero di biscotti"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="range"
                  min="6"
                  max="48"
                  step="2"
                  value={count}
                  data-testid="calculator-slider"
                  onChange={(e) => setCount(Number(e.target.value))}
                  className="frolla-slider flex-1"
                  style={{ "--fill": fill }}
                  aria-label="Numero di biscotti desiderati"
                />
                <button
                  data-testid="calculator-stepper-plus"
                  onClick={() => setCount((c) => Math.min(48, c + 2))}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-pink-200 bg-white text-rosa-dark transition-colors hover:bg-blush"
                  aria-label="Aumenta numero di biscotti"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-semibold text-cacao-light">
                <span>6</span>
                <span>48</span>
              </div>
              <p className="mt-2 text-[11px] text-cacao-light">
                Ogni biscotto tondo pesa circa 27-30 g: con la dose base ne escono 24.
              </p>
            </div>
          </div>

          <div data-testid="ingredient-list" className="rounded-2xl border border-pink-100 bg-crema/80 p-5">
            <p className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cacao-light">
              <Cookie className="h-4 w-4 text-rosa" /> Ingredienti per {count} biscotti
            </p>
            <ul className="divide-y divide-pink-100">
              {v.items.map((item) => (
                <li
                  key={item.name}
                  className="flex items-baseline justify-between gap-3 py-2.5"
                  data-testid={`ingredient-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="text-sm text-cacao-muted">{item.name}</span>
                  <motion.span
                    key={`${variant}-${count}-${item.name}`}
                    initial={{ opacity: 0.3, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-display text-lg font-bold text-cacao"
                  >
                    {scaleItem(item, count)}
                  </motion.span>
                </li>
              ))}
            </ul>
            <button
              data-testid="print-recipe-button"
              onClick={printRecipe}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-rosa/40 bg-white px-5 py-3 text-sm font-bold text-rosa-dark transition-colors hover:bg-blush"
            >
              <Printer className="h-4 w-4" />
              Stampa la ricetta · PDF
            </button>
            <p className="mt-2 text-center text-[11px] text-cacao-light">
              Include in omaggio la ricetta della pasta di zucchero del libro
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Ricette = ({ onExplore }) => (
  <div data-testid="section-ricette">
    <Hero onExplore={onExplore} />
    <Marquee />
    <section className="mx-auto max-w-md px-5 py-16 sm:max-w-2xl lg:max-w-5xl lg:px-8">
      <Reveal>
        <Calculator />
      </Reveal>
      <div className="mt-14">
        <Reveal>
          <div className="mb-8 flex items-center gap-3">
            <ChefHat className="h-5 w-5 text-rosa" />
            <h3 className="font-display text-xl font-bold text-cacao sm:text-2xl">
              Il metodo, in 4 mosse
            </h3>
          </div>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FROLLA_STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div
                data-testid={`frolla-step-${i + 1}`}
                className="h-full rounded-2xl border border-pink-100 bg-white p-5 transition-transform hover:-translate-y-1"
              >
                <p className="font-display text-3xl font-extrabold text-blush" style={{ WebkitTextStroke: "1px #E87A90" }}>
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-2 font-display text-base font-bold text-cacao">{s.title}</p>
                <p className="mt-1.5 text-sm text-cacao-muted leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-6 flex items-center gap-2 text-sm text-cacao-muted">
            <Clock className="h-4 w-4 text-oro" />
            Riposo in frigo: 30 minuti · Cottura: forno statico 180°C per 12-14 minuti
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div
          data-testid="pdz-recipe-card"
          className="mt-12 overflow-hidden rounded-[2rem] border border-amber-100 bg-burro/70 p-6 sm:p-10"
        >
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <Eyebrow>Bonus dal libro</Eyebrow>
              <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-cacao">
                La Pasta di Zucchero fatta in casa
              </h3>
              <p className="mt-2 text-sm text-cacao-muted leading-relaxed">
                La ricetta originale di «Biscotti Facili — Famiglia»: pochi
                ingredienti, un impasto elastico perfetto per coprire torte e
                modellare personaggi.
              </p>
              <ul className="mt-5 space-y-2">
                {PASTA_ZUCCHERO.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-cacao-muted" data-testid={`pdz-ingredient-${i + 1}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-oro" />
                    {ing}
                  </li>
                ))}
              </ul>
            </div>
            <ol className="space-y-3.5 self-center">
              {PASTA_ZUCCHERO.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3.5" data-testid={`pdz-step-${i + 1}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-oro font-display text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <p className="text-sm text-cacao-muted leading-relaxed">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Reveal>

      <div className="mt-16">
        <Reveal>
          <Eyebrow>Basi per le Torte</Eyebrow>
          <h3 className="mt-3 font-display text-xl font-bold text-cacao sm:text-2xl">
            Pan di Spagna & Torta Moretto
          </h3>
          <p className="mt-2 max-w-lg text-sm text-cacao-muted leading-relaxed">
            Le due basi indispensabili prima di coprire e decorare: dosi per
            una teglia da 20 cm.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {BASI_TORTE.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.08}>
              {b.locked ? (
                <MorettoCard />
              ) : b.calc ? (
                <PanSpagnaCard base={b} />
              ) : (
              <article
                data-testid={`base-torta-${b.id}`}
                className="h-full rounded-[1.75rem] border border-amber-100 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(45,36,36,0.1)] sm:p-8"
              >
                <span className="inline-flex items-center gap-1.5 rounded-full bg-menta px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-menta-dark">
                  Ricetta Gratuita
                </span>
                <h4 className="mt-3 font-display text-lg font-bold text-cacao">{b.title}</h4>
                <p className="mt-1 text-xs text-cacao-muted">{b.subtitle}</p>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-oro">Ingredienti</p>
                    <ul className="space-y-1.5">
                      {b.ingredients.map((ing, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-cacao-muted" data-testid={`base-${b.id}-ing-${j + 1}`}>
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rosa" />
                          {ing}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-oro">Procedimento</p>
                    <ol className="space-y-2">
                      {b.steps.map((step, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-sm text-cacao-muted leading-relaxed" data-testid={`base-${b.id}-step-${j + 1}`}>
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blush font-display text-[11px] font-bold text-rosa-dark">
                            {j + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
                <p className="mt-5 font-hand text-xl text-oro">{b.note}</p>
              </article>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default Ricette;
